import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, ShieldCheck, IdCard, Users, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";
import { REGISTRATION_LINKS, MEDIA } from "../lib/constants";

const FAQS = [
  {
    q: "How long does DC medical cannabis self-certification take?",
    a: "Most patients complete the DC self-certification in less than 10 minutes. You'll need a valid photo ID, an email address, and (for non-DC residents) a payment method for the temporary registration fee. Once approved, your digital patient registration is emailed to you and you can shop at District Wellness Center immediately.",
  },
  {
    q: "Do I need a doctor's recommendation to self-certify in Washington, DC?",
    a: "No. Washington, DC uses a self-certification model — adults 21+ do not need a doctor's visit, prescription, or physician sign-off. You certify online that you intend to use cannabis for medical purposes. That's the entire medical qualification.",
  },
  {
    q: "Is self-certification available to out-of-state visitors?",
    a: "Yes. Non-DC residents 21+ can self-certify online for a temporary DC patient registration starting at $10 for 3 days. Longer options are available (30, 90, 180, 365 days). You can also present a valid medical cannabis card from a participating reciprocity state instead.",
  },
  {
    q: "Where do I complete DC self-certification?",
    a: "Through the DC ABCA's myABCA portal — the single official entry point for DC medical cannabis registration. Our patient specialists can walk you through it at 4915 Georgia Ave NW if you'd rather do it in person.",
  },
  {
    q: "What ID do I need for self-certification?",
    a: "A valid government-issued photo ID that shows you are 21 or older. US driver's license, state ID, US passport, or military ID all work. Non-DC residents also need either their home-state medical cannabis card or their DC temporary patient registration confirmation.",
  },
  {
    q: "Is my self-certification information kept confidential?",
    a: "Yes. Patient registration data is protected under DC's medical cannabis program. DWC only accesses the parts of your registration needed to verify eligibility at the register — nothing more.",
  },
];

export default function SelfCertify() {
  return (
    <>
      <SEO
        title="Self-Certify in DC in Under 10 Minutes | District Wellness Center"
        description="Get your DC medical cannabis patient registration fast. Self-certify online in less than 10 minutes — fast, easy, confidential. DC residents and out-of-state visitors welcome at District Wellness Center."
        path="/self-certify"
        breadcrumbs={[{ name: "Self-Certify", path: "/self-certify" }]}
        faq={FAQS}
      />

      {/* HERO */}
      <section className="bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 py-20 sm:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">
              Safe · Legal · Medical
            </div>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Self-certify for DC <em className="text-brand">medical cannabis</em> in under 10 minutes.
            </h1>
            <p className="mt-6 text-ink-muted text-base sm:text-lg max-w-xl leading-relaxed">
              Washington, DC is one of the fastest jurisdictions in the country to access medical cannabis
              legally. No doctor visit. No prescription. Just a short online form through the myABCA portal.
              Fast, easy, confidential — and open to both DC residents and out-of-state visitors.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={REGISTRATION_LINKS.medical}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
                data-testid="sc-cta-register"
              >
                Start Self-Certifying <ArrowUpRight size={14} />
              </a>
              <Link
                to="/menu"
                className="inline-flex items-center gap-2 border border-ink/15 text-ink hover:border-brand hover:text-brand px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
                data-testid="sc-cta-menu"
              >
                Preview the menu <ArrowUpRight size={14} />
              </Link>
            </div>

            {/* Speed strip */}
            <div className="mt-10 flex items-center gap-4 p-5 bg-[#0F2F1F] text-white">
              <Clock size={28} className="text-brand shrink-0" />
              <div>
                <div className="font-serif text-2xl leading-tight">10 minutes</div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-white/70">
                  Fast · Easy · Confidential
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src={MEDIA.storefrontDoor}
              alt="District Wellness Center — licensed DC medical cannabis dispensary, 4915 Georgia Ave NW, Washington DC"
              className="w-full h-[520px] object-cover border border-black/10"
              loading="lazy"
              data-testid="sc-hero-image"
            />
          </div>
        </div>
      </section>

      {/* WHAT IT IS */}
      <article className="py-20 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-6 font-sans text-ink-muted">
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-0 mb-5">
            What is DC self-certification?
          </h2>
          <p className="text-lg leading-relaxed">
            Self-certification is Washington, DC&rsquo;s streamlined medical cannabis registration process.
            Adults 21 and older complete a short online form on the DC ABCA&rsquo;s <strong>myABCA
            portal</strong>, confirming that they intend to use cannabis for medical purposes. That&rsquo;s
            it. No doctor&rsquo;s appointment, no prescription, no letter — just a short digital form and a
            government-issued photo ID.
          </p>
          <p className="text-lg leading-relaxed mt-4">
            Once approved, your digital patient registration lands in your inbox in minutes. You can walk
            into <strong>District Wellness Center at 4915 Georgia Ave NW</strong> the same day, order for
            same-day delivery across DC, or reserve on our live menu for pickup.
          </p>

          {/* BENEFITS */}
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            Why patients choose to self-certify
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="p-6 bg-white border border-black/10">
              <Clock size={20} className="text-brand mb-3" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Fast</div>
              <div className="font-serif text-lg text-ink leading-tight">Under 10 minutes online</div>
            </div>
            <div className="p-6 bg-white border border-black/10">
              <ShieldCheck size={20} className="text-brand mb-3" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Confidential</div>
              <div className="font-serif text-lg text-ink leading-tight">Protected patient data</div>
            </div>
            <div className="p-6 bg-white border border-black/10">
              <IdCard size={20} className="text-brand mb-3" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Simple</div>
              <div className="font-serif text-lg text-ink leading-tight">ID + email. That&rsquo;s it.</div>
            </div>
          </div>

          {/* HOW IT WORKS */}
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            How self-certification works (step by step)
          </h2>
          <ol className="space-y-4 text-lg list-decimal pl-6">
            <li>
              <strong>Open the myABCA portal.</strong> It&rsquo;s the single official entry point for DC
              medical cannabis registration.
            </li>
            <li>
              <strong>Confirm you&rsquo;re 21+.</strong> You&rsquo;ll need a valid, government-issued photo
              ID (US driver&rsquo;s license, state ID, US passport, or military ID).
            </li>
            <li>
              <strong>Fill out the short self-certification form.</strong> No prescription, no doctor,
              nothing complicated — just basic information.
            </li>
            <li>
              <strong>Choose your registration period.</strong> DC residents register long-term. Non-DC
              residents can pick 3-day, 30-day, 90-day, 180-day, or 365-day temporary registrations
              starting at $10.
            </li>
            <li>
              <strong>Submit + receive your digital registration.</strong> Once approved, your digital
              patient ID is emailed to you and is ready to use.
            </li>
            <li>
              <strong>Visit DWC.</strong> Show your ID and your digital registration at the door at 4915
              Georgia Ave NW — or order same-day delivery from our menu.
            </li>
          </ol>

          <div className="mt-10">
            <a
              href={REGISTRATION_LINKS.medical}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 bg-ink hover:bg-brand text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
              data-testid="sc-cta-register-mid"
            >
              Start on myABCA <ArrowUpRight size={14} />
            </a>
          </div>

          {/* WHO IT'S FOR */}
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            Open to DC residents and out-of-state patients
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="p-8 bg-white border border-black/10">
              <IdCard size={22} className="text-brand mb-4" />
              <h3 className="font-serif text-2xl text-ink mb-3">DC residents</h3>
              <p className="text-ink-muted leading-relaxed">
                Adults 21+ living in Washington, DC can self-certify for long-term medical cannabis access
                in about 5–10 minutes. Valid DC ID required.
              </p>
            </div>
            <div className="p-8 bg-white border border-black/10">
              <Users size={22} className="text-brand mb-4" />
              <h3 className="font-serif text-2xl text-ink mb-3">Out-of-state visitors</h3>
              <p className="text-ink-muted leading-relaxed">
                Visiting from Maryland, Virginia, or anywhere else? Register as a temporary DC patient
                starting at $10 for 3 days — or use your home-state medical card if your state
                participates in reciprocity.
              </p>
            </div>
          </div>

          {/* WHAT YOU NEED */}
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            What you need before you start
          </h2>
          <ul className="space-y-3 text-lg">
            <li className="flex gap-3"><CheckCircle2 size={18} className="text-brand mt-1 shrink-0" /> A valid government-issued photo ID (proving you&rsquo;re 21+).</li>
            <li className="flex gap-3"><CheckCircle2 size={18} className="text-brand mt-1 shrink-0" /> An email address (your digital registration is emailed to you).</li>
            <li className="flex gap-3"><CheckCircle2 size={18} className="text-brand mt-1 shrink-0" /> A credit or debit card for the temporary registration fee (out-of-state visitors).</li>
            <li className="flex gap-3"><CheckCircle2 size={18} className="text-brand mt-1 shrink-0" /> Approximately 5–10 minutes.</li>
          </ul>

          {/* FAQ */}
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            Self-certification FAQ
          </h2>
          <div className="space-y-6">
            {FAQS.map((f) => (
              <div key={f.q} className="border-b border-black/10 pb-6">
                <div className="font-serif text-xl text-ink mb-2">{f.q}</div>
                <p className="text-ink-muted leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>

          {/* Related links */}
          <div className="mt-16 pt-10 border-t border-black/10 grid sm:grid-cols-3 gap-6">
            <Link to="/blog/how-to-buy-medical-cannabis-dc" className="group block" data-testid="sc-related-buy">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">Related</div>
              <div className="font-serif text-xl text-ink group-hover:text-brand">How to buy medical cannabis in DC →</div>
            </Link>
            <Link to="/blog/out-of-state-dispensary-dc" className="group block" data-testid="sc-related-oos">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">Related</div>
              <div className="font-serif text-xl text-ink group-hover:text-brand">Out-of-state visitor guide →</div>
            </Link>
            <Link to="/menu" className="group block" data-testid="sc-related-menu">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">Related</div>
              <div className="font-serif text-xl text-ink group-hover:text-brand">Shop the live menu →</div>
            </Link>
          </div>
        </div>
      </article>

      {/* FINAL CTA */}
      <section className="bg-ink text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05]">
            Your journey to wellness starts here.
          </h2>
          <p className="mt-5 text-white/70 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Self-certify online in under 10 minutes — then walk into 4915 Georgia Ave NW or order same-day
            delivery across DC. Safe. Legal. Medical.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={REGISTRATION_LINKS.medical}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em]"
              data-testid="sc-final-register"
            >
              Register on myABCA <ArrowUpRight size={14} />
            </a>
            <Link
              to="/locations"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-brand hover:text-brand text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em]"
              data-testid="sc-final-directions"
            >
              Directions to DWC
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
